
import { regular, solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { memo, useEffect, useState } from "react";
import { Accordion, Alert, Col, ListGroup, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Metrics } from "../api/Metrics";
import AppLoader from "./AppLoader";
import AppSearch from "./AppSearch";
import IconButton from "./IconButton";
import { useToasts } from "../contexts/toasts";
import { Task } from "../models/Task"
import { Metric } from "../models/Metric";
import "./TaskMetricsComponent.scss"


type MetricItemType = {
    metric: Metric;
    actions: React.ReactNode;
};

const MetricItem = memo(({ metric, actions }: MetricItemType) => {
    return (
        <ListGroup.Item className="d-flex align-items-center p-1">
            <Col md={1} className="me-2">
                <FontAwesomeIcon icon={regular("check-circle")} size="lg" fixedWidth />
            </Col>
            <Col className="text-start">
                <h6 className="mb-0">{metric.name}</h6>
            </Col>
            <Col md={5} className="text-end">
                {actions}
            </Col>
        </ListGroup.Item>
    );
});

type Props = {
    initialTask: Task;
    metricList: Metric[]
    setMetriclist: React.Dispatch<React.SetStateAction<Metric[]>>;
};

export default function TaskMetricsComponent({ initialTask, metricList, setMetriclist }: Props) {
    const { t } = useTranslation();
    const { id } = useParams();


    const [metrics, setMetrics] = useState<Metric[] | null>();

    const [searchText, setSearchText] = useState<string>("");

    const { showToast } = useToasts();

    useEffect(() => {
        if (id || initialTask) {
            initMetrics();
        }   // eslint-disable-next-line
    }, [id]);

    const initMetrics = () => {
        Metrics.getMetrics().then(
            ({ data: metrics }) => {
                setMetrics(metrics)
                initTask(metrics)
            },
            (err) => {
                const message = err.response?.data?.message || err.message;
                const status = err.response?.status;
                showToast!(
                    "metrics-error",
                    message,
                    `Metrics Error ${status ? `(${status})` : ""}`,
                    "danger"
                );
            }
        );
    };

    const initTask = (metrics: Metric[]) => {
        if (initialTask.metricTypes && initialTask.metricTypes.length > 0) {
            let mappedTask = initialTask.metricTypes!.map((mId) => { return metrics?.find((met) => met.id === mId)! }) || []
            setMetriclist([...mappedTask]);
        } else {
            initialTask.metricTypes = [];
            setMetriclist([]);
        }
    };


    const swapMetrics =
        (metricIndex: number, direction: "up" | "down") => () => {
            let newMetricList = [...metricList];
            const target = direction === "down" ? metricIndex + 1 : metricIndex - 1;

            let metric = newMetricList.splice(
                metricIndex,
                1,
            )[0]

            newMetricList.splice(
                target,
                0,
                metric
            )

            setMetriclist([...newMetricList]);
        };

    const removeTask = (metric: number) => () => {
        let newMetricLists = [...metricList];
        newMetricLists.splice(metric, 1);

        setMetriclist([...newMetricLists]);
    };


    const renderTaskMetrics = () => {

        return (
            <Accordion
                className="sessions-accordions"
                defaultActiveKey={"1"}
                alwaysOpen
            >

                <Accordion.Item eventKey={"1"}>
                    <Accordion.Header className="d-flex">
                        <Col>{initialTask.name}</Col>
                    </Accordion.Header>

                    <Accordion.Body className="p-1">
                        {metricList.length === 0 && (
                            <Alert variant="warning" className="py-2 mb-0">
                                <FontAwesomeIcon icon={solid("info-circle")} />
                                <span className="ms-2">No metrics in this metricList.</span>
                            </Alert>
                        )}

                        <ListGroup className="metric-pick-list">

                            {metricList.map((metric, i) => {
                                const met = metric as Metric

                                return (
                                    <MetricItem
                                        key={i}
                                        metric={met}
                                        actions={
                                            <>
                                                {i < metricList.length - 1 && (
                                                    <IconButton type="button" onClick={swapMetrics(i, "down")}>
                                                        <FontAwesomeIcon icon={solid("arrow-down")} />
                                                    </IconButton>
                                                )}
                                                {i > 0 && (
                                                    <IconButton type="button"
                                                        className="ms-2"
                                                        onClick={swapMetrics(i, "up")}
                                                    >
                                                        <FontAwesomeIcon icon={solid("arrow-up")} />
                                                    </IconButton>
                                                )}
                                                <IconButton type="button"
                                                    className="ms-2 text-danger"
                                                    onClick={removeTask(i)}
                                                >
                                                    <FontAwesomeIcon icon={solid("times")} />
                                                </IconButton>
                                            </>
                                        }
                                    />
                                )
                            })}
                        </ListGroup>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
        );
    };

    const addMetric = (metric: Metric) => () => {

        const newMetricList = [...metricList];

        const found = metricList.findIndex(
            (t) => t.id === metric.id
        );
        if (found > -1) {
            showToast!(
                "metric-warning",
                "Metric already present in this metricList.",
                "Existing Metric",
                "warning"
            );
            return;
        }

        newMetricList.push(metric);

        setMetriclist([...newMetricList]);


    };

    const handleSearchChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
        const value = evt.target.value;
        setSearchText(value);
    };

    const renderMetrics = () => {
        if (metrics!.length === 0)
            return (
                <Alert className="py-2 mb-0">
                    <FontAwesomeIcon icon={solid("info-circle")} />
                    <span className="ms-2">{t("metricsList.noMetrics")}</span>
                </Alert>
            );

        return (
            <>
                <AppSearch
                    className="mb-3"
                    value={searchText}
                    onChange={handleSearchChange}
                />
                <ListGroup className="metric-pick-list">
                    {metrics!
                        .filter((metric) =>
                            metric.name.toLowerCase().includes(searchText.toLowerCase())
                        ).filter((met) => !metricList.includes(met) )
                        .map((metric, index) => (
                            <MetricItem
                                key={index}
                                metric={metric}
                                actions={
                                    <IconButton type="button" className="text-success" onClick={addMetric(metric)}>
                                        <FontAwesomeIcon icon={solid("plus")} />
                                    </IconButton>
                                }
                            />
                        ))}
                </ListGroup>
            </>
        );
    };

    const renderCardContent = () => {
        return (
            <>

                <Row>
                    <Col>
                        {renderTaskMetrics()}
                    </Col>
                    <Col>
                        {renderMetrics()}
                    </Col>
                </Row>


            </>
        );
    };

    const renderAsyncContent = () => {

        return metrics && metricList ? (
            renderCardContent()
        ) : (
            <div className="d-flex py-3 justify-content-center">
                <AppLoader size={150} />
            </div>
        );
    };

    return (
        <>
            {renderAsyncContent()}
        </>
    );
}


